import React, { useState, useEffect, useRef } from "react";
import {
  Editor,
  EditorState,
  Modifier,
  RichUtils,
  CompositeDecorator,
  convertToRaw,
  convertFromRaw,
} from "draft-js";
import { convertToHTML, convertFromHTML } from "draft-convert";
import DOMPurify from "dompurify";
import "draft-js/dist/Draft.css";
import Toolbar from "./ToolBar";
import CustomToolbar from "./ToolBar";
import { toast } from "react-toastify";
import { Mp3Encoder } from "lamejs";
import convertToMp3 from "../../Utils/convertToMp3";
import { useLocalDraftJson } from "../../hooks/useLocalDraft";
import {
  MENTION_ENTITY,
  mentionEntityStrategy,
  MentionSpan,
} from "../draftMention";
import {
  preserveBlankLines,
  preserveExtraSpaces,
} from "../../Utils/preserveEditorWhitespace";

const mentionDecorator = new CompositeDecorator([
  { strategy: mentionEntityStrategy, component: MentionSpan },
]);

const getNodeAttr = (node, name) =>
  node.getAttribute?.(name) ?? node.attribs?.[name];
const getNodeClass = (node) => getNodeAttr(node, "class") || "";
const getNodeText = (node) =>
  node.textContent ?? (node.children || []).map((c) => c.data || "").join("");

// Parse saved HTML (e.g. when editing) and reconstruct MENTION entities
// from <span class="comment-mention" data-user-id="..."> tags.
const mentionConvertFromHTML = (html) =>
  convertFromHTML({
    htmlToEntity: (nodeName, node, createEntity) => {
      if (
        nodeName === "span" &&
        getNodeClass(node).includes("comment-mention")
      ) {
        const userId = getNodeAttr(node, "data-user-id") || "";
        const text = getNodeText(node);
        const fullName = text.startsWith("@") ? text.slice(1).trim() : text;
        return createEntity(MENTION_ENTITY, "IMMUTABLE", {
          userId: String(userId),
          fullName: fullName || "",
        });
      }
      return null;
    },
  })(html);

const ChatMessageBox = ({
  onSendClick,
  onSendFile, // New prop for file sending
  isExpandedByDefault,
  initialText = "",
  isEditing = false,
  onCancel,
  replyingTo = null,
  onCancelReply,
  disabledButton,
  participants = {},
  userProfile,
  conversationId,
}) => {
  const messageDraftEnabled = Boolean(
    userProfile?.id && conversationId && !isEditing,
  );

  const {
    value: draftRawContent,
    setValue: persistMessageDraft,
    clearDraft: clearMessageDraft,
  } = useLocalDraftJson({
    userId: userProfile?.id,
    scopeId: conversationId,
    fieldKey: "chat.message",
    enabled: messageDraftEnabled,
    initialValue: null,
    syncValueToState: false,
  });

  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault || false);
  const [imageUrl, setImageUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  // const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [textInput, setTextInput] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);
  const textInputRef = useRef(null);
  const [editorState, setEditorState] = useState(
    EditorState.createEmpty(mentionDecorator),
  );
  const [showFooter, setShowFooter] = useState(false);
  const editorContainerRef = useRef(null);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([
    "Alice",
    "Bob",
    "Charlie",
  ]);

  const [recordedUrl, setRecordedUrl] = useState("");
  const mediaStream = useRef(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const mentionDropdownRef = useRef(null);

  const decodeEntitiesRecursively = (text = "", maxIterations = 3) => {
    if (!text) return "";
    let decoded = text;
    let previous = text;
    let iterations = 0;

    do {
      previous = decoded;
      const doc = new DOMParser().parseFromString(decoded, "text/html");
      decoded = doc.body.innerHTML || "";
      iterations += 1;
    } while (decoded !== previous && iterations < maxIterations);

    return decoded;
  };

  //convert participants object into arrays
  const participantList = Object.entries(participants)
    .map(([id, p]) => ({
      id,
      name: p.name,
    }))
    .filter((p) => p.id != userProfile?.id);

  const handleTextFocus = () => {
    setTextInput("");
    setIsExpanded(true);
    setTimeout(() => focusChatEditor(), 0);
  };

  const focusChatEditor = () => {
    const contentEditable = editorContainerRef.current?.querySelector(
      ".public-DraftEditor-content",
    );
    contentEditable?.focus?.();
  };

  const handleCloseEditor = () => {
    setIsExpanded(false);
    // Clear file selection when closing
    setSelectedFile(null);
    setImageUrl(null);
    setEditorState(EditorState.createEmpty(mentionDecorator));
    // If in editing mode, call the onCancel prop
    if (isEditing && onCancel) {
      onCancel();
    }

    if (replyingTo && onCancelReply) {
      onCancelReply();
    }
  };

  const messageBoxHeight = isExpandedByDefault
    ? "250px"
    : isExpanded
      ? "550px"
      : "auto";

  // Handle file upload with preview
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);

      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = function (e) {
          setImageUrl(e.target.result);
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith("video/")) {
        const reader = new FileReader();
        reader.onload = function (e) {
          setImageUrl(e.target.result);
        };
      } else {
        setImageUrl(null);
      }

      // Expand the editor when file is selected
      setIsExpanded(true);
    }
  };

  // Remove file attachment
  const removeFileAttachment = () => {
    setSelectedFile(null);
    setRecordedUrl(null);
    setImageUrl(null);
    setIsExpanded(false);
  };

  //close mention dropdown when user click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mentionDropdownRef.current &&
        !mentionDropdownRef.current.contains(event.target)
      ) {
        setShowMentionDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (replyingTo) {
      setIsExpanded(true);
    }
  }, [replyingTo]);

  // Handle audio recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      mediaStream.current = stream;
      mediaRecorder.current = new MediaRecorder(stream);

      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.current.push(e.data);
        }
      };

      mediaRecorder.current.onstop = async () => {
        try {
          const recordedBlob = new Blob(chunks.current, {
            type: chunks.current[0]?.type || "audio/webm",
          });

          // Convert to MP3 for better iOS compatibility using shared helper
          const mp3File = await convertToMp3(recordedBlob);
          if (recordedUrl) URL.revokeObjectURL(recordedUrl);
          const url = URL.createObjectURL(
            mp3File instanceof Blob
              ? mp3File
              : new Blob([mp3File], { type: mp3File.type || "audio/mpeg" }),
          );
          const audioFile =
            mp3File instanceof File
              ? mp3File
              : new File([mp3File], `audio_${Date.now()}.mp3`, {
                  type: "audio/mpeg",
                });

          setRecordedUrl(url);
          chunks.current = [];

          setSelectedFile(audioFile);
          setAudioChunks([]);
          setIsRecording(false);
          setIsExpanded(true);
        } catch (error) {
          console.error("Error processing recorded audio:", error);
          // fallback: create file from raw chunks
          try {
            const fallbackBlob = new Blob(chunks.current, {
              type: "audio/webm",
            });
            if (recordedUrl) URL.revokeObjectURL(recordedUrl);
            const url = URL.createObjectURL(fallbackBlob);
            const audioFile = new File(
              [fallbackBlob],
              `audio_${Date.now()}.webm`,
              { type: "audio/webm" },
            );
            setRecordedUrl(url);
            chunks.current = [];
            setSelectedFile(audioFile);
            setAudioChunks([]);
            setIsRecording(false);
            setIsExpanded(true);
          } catch (err2) {
            console.error("Fallback also failed", err2);
            setIsRecording(false);
          }
        }
      };

      mediaRecorder.current.start();
      setIsRecording(true);

      const icon = document.getElementById("audioIcon");
      if (icon) {
        icon.style.filter =
          "invert(21%) sepia(100%) saturate(7414%) hue-rotate(359deg) brightness(94%) contrast(117%)";
      }

      // setMediaRecorder(recorder);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      alert(
        "Click to the icon next to empoweredfe.s3-website-ap-southeast-2.amazonaws.com and open site settings and give access to microphone",
      );
      // toast.error("Unable to access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    // if (mediaRecorder && mediaRecorder.state !== "inactive") {
    //   mediaRecorder.stop();
    //   const tracks = mediaRecorder.stream.getTracks();
    //   tracks.forEach((track) => track.stop());
    //   setMediaRecorder(null);
    // }
    if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
      mediaRecorder.current.stop();
    }
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => {
        track.stop();
      });
    }
  };
  const handleVoiceRecording = () => {
    if (!isRecording) {
      startRecording();
    } else {
      stopRecording();
      const icon = document.getElementById("audioIcon");
      if (icon) {
        icon.style.filter = "none";
      }
    }
  };

  // Handle text input change
  const handleTextChange = (e) => {
    setTextInput(e.target.value);
  };

  // Edit mode: load from server. Otherwise: restore per-chat draft from localStorage (per user).
  useEffect(() => {
    if (isEditing && initialText) {
      try {
        const normalizedHtml = decodeEntitiesRecursively(initialText);

        const safeHtml = DOMPurify.sanitize(normalizedHtml, {
          ADD_ATTR: ["data-user-id"],
        });

        const contentState = mentionConvertFromHTML(safeHtml);

        setEditorState(
          EditorState.createWithContent(contentState, mentionDecorator),
        );
        setIsExpanded(true);
      } catch (error) {
        setEditorState(EditorState.createEmpty(mentionDecorator));
        setIsExpanded(true);
      }
    } else if (!messageDraftEnabled) {
      setEditorState(EditorState.createEmpty(mentionDecorator));
    } else if (draftRawContent) {
      try {
        setEditorState(
          EditorState.createWithContent(
            convertFromRaw(draftRawContent),
            mentionDecorator,
          ),
        );
      } catch {
        setEditorState(EditorState.createEmpty(mentionDecorator));
      }
    } else {
      setEditorState(EditorState.createEmpty(mentionDecorator));
    }
  }, [
    initialText,
    isEditing,
    conversationId,
    draftRawContent,
    messageDraftEnabled,
  ]);

  useEffect(() => {
    if (!isEditing) {
      setSelectedFile(null);
      setImageUrl(null);
      setRecordedUrl(null);
      setIsExpanded(isExpandedByDefault || false);
    }
  }, [conversationId]);

  // Toggle emoji picker visibility
  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleEmojiSelect = (event) => {
    event.stopPropagation();
    const emoji = event.detail.unicode;

    setEditorState((prev) => {
      const contentState = prev.getCurrentContent();
      const selectionState = prev.getSelection();

      const newContentState = Modifier.insertText(
        contentState,
        selectionState,
        emoji,
      );

      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        "insert-characters",
      );

      return newEditorState;
    });
  };

  // Load emoji-picker-element script dynamically
  useEffect(() => {
    if (!document.querySelector('script[src*="emoji-picker-element"]')) {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/emoji-picker-element@^1/index.js";
      script.type = "module";
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (!showEmojiPicker || !emojiPickerRef.current) return;

    const emojiPickerElement = emojiPickerRef.current;
    emojiPickerElement.addEventListener("emoji-click", handleEmojiSelect);

    return () => {
      emojiPickerElement.removeEventListener("emoji-click", handleEmojiSelect);
    };
  }, [showEmojiPicker]);

  // Click outside to close emoji picker
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        event.target.id !== "emojiButton"
      ) {
        setShowEmojiPicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    let timeout = null;
    if (isExpanded) {
      timeout = setTimeout(() => {
        setShowFooter(true);
      }, 100);
    } else {
      setShowFooter(false);
    }
    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [isExpanded]);

  const cleanHTML = (html) => {
    const convertedHtml = convertToHTML({
      blockToHTML: (block) => {
        if (block.type === "unstyled" && block.text.length === 0) {
          return (
            <p>
              <br />
            </p>
          );
        }
        return null;
      },
      entityToHTML: (entity, originalText) => {
        if (entity.type === MENTION_ENTITY && entity.data?.userId) {
          const displayText = originalText || `@${entity.data.fullName || ""}`;
          return `<span class="comment-mention" data-user-id="${entity.data.userId}">${displayText}</span>`;
        }
        return originalText;
      },
    })(editorState.getCurrentContent());

    let cleaned = preserveBlankLines(preserveExtraSpaces(convertedHtml))
      .replace(
        /<span(?![^>]*class="comment-mention")[^>]*>(\s|&nbsp;)*<\/span>/g,
        "",
      )
      .trim();

    if (cleaned && !cleaned.startsWith("<p>")) {
      cleaned = `<p>${cleaned}</p>`;
    }

    return cleaned;
  };

  const hasMeaningfulText = (content) => {
    const plainText = content
      .getPlainText("\n")
      .replace(/\u00A0/g, " ")
      .trim();
    return plainText.length > 0;
  };

  const handleSend = async () => {
    const content = editorState.getCurrentContent();
    const hasText = hasMeaningfulText(content);
    const hasFile = selectedFile !== null;

    if (!hasText && !hasFile) {
      toast.error("Enter message or select a file");
      return;
    }
    setIsExpanded(false);

    try {
      // Send file if selected
      if (hasFile && onSendFile) {
        await onSendFile(selectedFile);
      }

      // Extract mentions and message text
      let messageToSend = "";
      let mentionedIds = [];

      if (hasText) {
        messageToSend = cleanHTML(content); // your current HTML cleaning function

        // Find all mention IDs
        const blocks = content.getBlocksAsArray();

        blocks.forEach((block) => {
          block.findEntityRanges(
            (char) => {
              const entityKey = char.getEntity();
              return (
                entityKey !== null &&
                content.getEntity(entityKey).getType() === MENTION_ENTITY
              );
            },
            (start, end) => {
              const entityKey = block.getEntityAt(start);
              const entity = content.getEntity(entityKey);
              const { userId } = entity.getData();
              if (userId) mentionedIds.push(userId);
            },
          );
        });

        // Remove duplicate IDs
        mentionedIds = [...new Set(mentionedIds)];
      }

      // Call the send callback with message and mentions
      if (onSendClick) {
        onSendClick(messageToSend, selectedFile, mentionedIds);
      }

      // Reset the editor and file selection
      if (messageDraftEnabled) {
        clearMessageDraft();
      }
      setEditorState(EditorState.createEmpty(mentionDecorator));
      setTimeout(() => {
        focusChatEditor();
      }, 50);

      setSelectedFile(null);
      setImageUrl(null);
      setRecordedUrl(null);
      setIsExpanded(false);

      if (replyingTo && onCancelReply) {
        onCancelReply();
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
  };

  // Get file type icon
  const getFileIcon = (file) => {
    if (!file) return null;

    if (file.type.startsWith("image/")) return "🖼️";
    if (file.type.startsWith("audio/")) return "🎵";
    if (file.type.startsWith("video/")) return "🎥";
    if (file.type.includes("pdf")) return "📄";
    if (file.type.includes("word") || file.type.includes("doc")) return "📝";
    if (file.type.includes("excel") || file.type.includes("sheet")) return "📊";
    return "📎";
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // toolbar
  const toggleInlineStyle = (style) => {
    if (disabledButton) return;
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlockType = (blockType) => {
    if (disabledButton) return;
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };

  //enter and send message
  const handleKeyCommand = (command, editorState) => {
    if (command === "send-message") {
      handleSend();
      return "handled";
    }
    try {
      const newState = RichUtils.handleKeyCommand(editorState, command);
      if (newState) {
        setEditorState(newState);
        return "handled";
      }
    } catch (error) {
      console.warn("Error handling key command:", error);
    }
    return "not-handled";
  };

  const getCurrentWord = (editorState) => {
    const selection = editorState.getSelection();
    const anchorKey = selection.getAnchorKey();
    const content = editorState.getCurrentContent();
    const block = content.getBlockForKey(anchorKey);
    const text = block.getText().slice(0, selection.getStartOffset());
    const match = text.match(/@\w*$/);
    return match ? match[0] : null;
  };

  const handleBeforeInput = (chars) => {
    if (chars === "@") {
      setShowMentionDropdown(true);
      setMentionSuggestions(participantList);
      return "not-handled"; // allow typing
    }
    return "not-handled";
  };

  const insertMention = (mention) => {
    const content = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const word = getCurrentWord(editorState);

    const contentWithEntity = content.createEntity(
      MENTION_ENTITY,
      "IMMUTABLE",
      {
        userId: String(mention.id),
        fullName: mention.name,
      },
    );
    const entityKey = contentWithEntity.getLastCreatedEntityKey();

    const newContent = Modifier.replaceText(
      contentWithEntity,
      selection.merge({
        anchorOffset: selection.getStartOffset() - (word ? word.length : 0),
      }),
      `@${mention.name}`,
      null,
      entityKey,
    );

    // Insert a trailing space *outside* the mention entity so further typing
    // is not absorbed into the chip.
    const contentWithSpace = Modifier.insertText(
      newContent,
      newContent.getSelectionAfter(),
      " ",
    );

    setEditorState(
      EditorState.push(editorState, contentWithSpace, "insert-characters"),
    );
    setShowMentionDropdown(false);
  };

  const updateMentionSuggestions = (state) => {
    const currentWord = getCurrentWord(state);
    if (currentWord && currentWord.startsWith("@")) {
      const query = currentWord.slice(1).toLowerCase();
      const filtered = participantList.filter((p) =>
        p.name.toLowerCase().startsWith(query),
      );
      setMentionSuggestions(filtered);
      setShowMentionDropdown(true);
    } else {
      setShowMentionDropdown(false);
    }
  };

  const handleEditorChange = (state) => {
    setEditorState(state);
    updateMentionSuggestions(state);
    if (messageDraftEnabled) {
      const content = state.getCurrentContent();
      if (content.hasText()) {
        persistMessageDraft(convertToRaw(content));
      } else {
        clearMessageDraft();
      }
    }
  };

  return (
    <div
      // id="messageBox"
      className="message-mt"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#1c2a37",
        borderRadius: "24px",
        transition: "height 0.3s ease-in-out, width 0.3s ease-in-out",
        position: "sticky",
        bottom: "0px",
        width: "100%",
        height: messageBoxHeight,
        border: isEditing
          ? "2px solid #4CAF50"
          : replyingTo
            ? "2px solid #2196F3"
            : "none",
      }}
    >
      {/* Add a header when in edit mode */}
      {isEditing && (
        <div
          style={{
            padding: "10px 20px",
            backgroundColor: "#2a4052",
            width: "100%",
            borderRadius: "24px 24px 0 0",
            color: "#fff",
          }}
        >
          Editing message...
        </div>
      )}

      {/* Add a header when in reply mode */}
      {replyingTo && (
        <div
          style={{
            padding: "10px 20px",
            backgroundColor: "#1e3a8a",
            width: "100%",
            borderRadius: isEditing || replyingTo ? "0" : "24px 24px 0 0",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: "20px",
          }}
        >
          <div>
            <div
              style={{ fontSize: "12px", opacity: 0.8, marginBottom: "10px" }}
            >
              Reply to
            </div>
            <div
              className="no-margin"
              style={{
                wordBreak: "break-all",
              }}
              dangerouslySetInnerHTML={{
                __html: `${replyingTo?.parentMessage?.content}`,
              }}
            ></div>
            <div style={{ fontSize: "14px", marginTop: "2px" }}>
              {replyingTo.content?.length > 50
                ? replyingTo.content.substring(0, 50) + "..."
                : replyingTo.content}
            </div>
          </div>
          <button
            onClick={onCancelReply}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: "18px",
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* File Preview Section */}
      {selectedFile && (
        <>
          <div
            style={{
              width: "100%",
              padding: "10px 20px",
              backgroundColor: "#2a4052",
              borderRadius: isEditing ? "0" : "24px 24px 0 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "20px" }}>
                {getFileIcon(selectedFile)}
              </span>
              <div>
                <div style={{ color: "#fff", fontSize: "14px" }}>
                  {selectedFile?.name}
                </div>
                <div style={{ color: "#b3ddff", fontSize: "12px" }}>
                  {formatFileSize(selectedFile?.size)}
                </div>
              </div>
            </div>
            <button
              onClick={removeFileAttachment}
              style={{
                background: "none",
                border: "none",
                color: "#ff6b6b",
                cursor: "pointer",
                fontSize: "18px",
              }}
            >
              ✕
            </button>
          </div>
          {recordedUrl && (
            <div className="mt-4">
              <audio controls src={recordedUrl} className="mt-2 w-full" />
            </div>
          )}
        </>
      )}

      {/* Image Preview */}
      {imageUrl && (
        <div
          style={{
            width: "100%",
            padding: "10px 20px",
            backgroundColor: "#2a4052",
          }}
        >
          <img
            src={imageUrl}
            alt="Preview"
            style={{
              maxWidth: "200px",
              maxHeight: "150px",
              borderRadius: "8px",
              objectFit: "cover",
            }}
          />
        </div>
      )}

      {/* EDITOR vs TEXT INPUT */}
      <div
        style={{
          width: "100%",
          padding: "0 20px",
          flexGrow: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        {/* Rich Text Editor */}
        <div
          className="expanded-container"
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
            position: "relative",
          }}
        >
          {isExpanded ? (
            <CustomToolbar
              key={disabledButton ? "disabled" : "enabled"}
              editorState={editorState}
              onToggleInlineStyle={toggleInlineStyle}
              onToggleBlockType={toggleBlockType}
              disabledButton={disabledButton}
            />
          ) : (
            <SimpleToolbar
              handleTextFocus={handleTextFocus}
              showEmojiPicker={showEmojiPicker}
              emojiPickerRef={emojiPickerRef}
              toggleEmojiPicker={toggleEmojiPicker}
              handleFileUpload={handleFileUpload}
              handleVoiceRecording={handleVoiceRecording}
              isRecording={isRecording}
              disabledButton={disabledButton}
            />
          )}
          <hr
            id="horizontal-line"
            style={{
              width: "100%",
              border: "1px solid #2a4052",
              opacity: 1,
              margin: "10px 0",
            }}
          />

          <div
            ref={editorContainerRef}
            className="editor-container commentEditor"
            style={{
              flex: 1,
              minHeight: 0,
              padding: "8px 0px",
              borderRadius: "8px",
              color: "#C8CED3",
              cursor: disabledButton ? "not-allowed" : "text",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
              transition: "height 0.3s ease",
              position: "relative",
            }}
            onClick={() => {
              if (!disabledButton) {
                focusChatEditor();
              }
            }}
          >
            <Editor
              key={disabledButton ? "sending" : "active"}
              editorState={editorState}
              onChange={handleEditorChange}
              readOnly={disabledButton}
              placeholder={
                disabledButton
                  ? "Sending..."
                  : replyingTo
                    ? "Type your reply..."
                    : "Type your answer..."
              }
              editorStyle={{
                flex: 1,
                minHeight: isExpanded ? "100px" : "44px",
                maxHeight: isExpanded ? "400px" : "120px",
                overflowY: "auto",
              }}
              handleKeyCommand={handleKeyCommand}
              handleReturn={(e) => {
                if (disabledButton) return "handled";

                if (e.shiftKey) {
                  return "not-handled";
                }

                if (isExpanded) {
                  return "not-handled";
                }

                const hasText = hasMeaningfulText(
                  editorState.getCurrentContent(),
                );

                if (hasText) {
                  handleSend();
                  return "handled";
                }
                toast.error("Enter message");
                return "handled";
              }}
              handleBeforeInput={(chars) => handleBeforeInput(chars)}
            />
          </div>

          {showMentionDropdown && mentionSuggestions.length > 0 && (
            <ul
              ref={mentionDropdownRef}
              style={{
                position: "absolute",
                bottom: isExpanded ? "200px" : "110px",
                left: "20px",
                background: "#2a4052",
                color: "#fff",
                borderRadius: "6px",
                listStyle: "none",
                padding: "5px 0",
                margin: 0,
                zIndex: 1000,
                width: "250px",
              }}
            >
              {mentionSuggestions.map((user) => (
                <li
                  key={user.id}
                  style={{
                    padding: "5px 10px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onClick={() => insertMention(user)}
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>{user.name}</span>
                    <span style={{ fontSize: "12px", opacity: 0.7 }}>
                      {user.email}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {showFooter ? (
            <ChatFooter
              handleCloseEditor={handleCloseEditor}
              handleSend={handleSend}
              isEditing={isEditing}
              isReplying={!!replyingTo}
              disabledButton={disabledButton}
            />
          ) : null}
        </div>
        {!isExpanded ? (
          <button
            id="sendButton"
            disabled={disabledButton}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              marginLeft: "10px",
              alignItems: "center",
              display: "inline-block",
              bottom: "10px",
              right: 0,
              cursor: disabledButton ? "not-allowed" : "pointer",
              pointerEvents: disabledButton ? "none" : "auto",
              opacity: disabledButton ? 0.5 : 1,
            }}
            onClick={handleSend}
          >
            <img src="/icons/telegram.svg" alt="Send" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

const EMOJI_PICKER_THEME = {
  "--background": "#121e29",
  "--border-color": "#444",
  "--border-radius": "20px",
  "--input-font-color": "#ffffff",
  "--input-placeholder-color": "rgba(255, 255, 255, 0.55)",
  "--input-border-color": "#444",
  "--button-hover-background": "#484848",
  "--button-active-background": "#555555",
  "--category-font-color": "#efefef",
};

const applyEmojiPickerTheme = (picker) => {
  if (!picker) return false;

  picker.classList.add("dark");
  Object.entries(EMOJI_PICKER_THEME).forEach(([prop, value]) => {
    picker.style.setProperty(prop, value);
  });

  return true;
};

const SimpleToolbar = ({
  toggleEmojiPicker,
  handleTextFocus,
  emojiPickerRef,
  showEmojiPicker,
  handleFileUpload,
  handleVoiceRecording,
  isRecording,
  disabledButton = false,
}) => {
  const fileInputRef = useRef(null);

  useEffect(() => {
    let intervalId;

    const tryApplyTheme = () => {
      if (!applyEmojiPickerTheme(emojiPickerRef.current)) return;
      if (intervalId) clearInterval(intervalId);
    };

    tryApplyTheme();
    intervalId = setInterval(tryApplyTheme, 50);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [showEmojiPicker, emojiPickerRef]);

  return (
    <div
      id="iconContainer"
      style={{
        paddingTop: "12px",
        display: "flex",
        gap: "20px",
        width: "100%",
      }}
      className="position-relative ps-3 ps-md-4"
    >
      {/* File upload */}
      {/* <div>
        <input
          type="file"
          id="file-upload"
          style={{ display: "none" }}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
          onChange={handleFileUpload}
        />
        <img
          src="/icons/addmore.svg"
          alt=""
          style={{ cursor: "pointer" }}
          onClick={() => document.getElementById("file-upload").click()}
        />
      </div> */}

      {/* Audio button */}
      <div
        id="thirdvoiceButton"
        style={{
          cursor: disabledButton ? "not-allowed" : "pointer",
          pointerEvents: disabledButton ? "none" : "auto",
          opacity: disabledButton ? 0.5 : 1,
        }}
        onClick={!disabledButton ? handleVoiceRecording : null}
      >
        <img
          id="audioIcon"
          src="/icons/audioicon.svg"
          alt="Audio"
          style={{
            background: isRecording ? "green" : "none",
            borderRadius: "50%",
            filter: isRecording
              ? "invert(21%) sepia(100%) saturate(7414%) hue-rotate(359deg) brightness(94%) contrast(117%)"
              : "none",
          }}
        />
      </div>

      {/* Text icon */}
      <div>
        <img
          src="/icons/texticon.svg"
          alt=""
          style={{
            cursor: disabledButton ? "not-allowed" : "pointer",
            pointerEvents: disabledButton ? "none" : "auto",
            opacity: disabledButton ? 0.5 : 1,
          }}
          onClick={!disabledButton ? handleTextFocus : null}
        />
      </div>

      {/* Emoji picker */}
      <div
        style={{
          position: "absolute",
          bottom: "35px",
          left: "0px",
          display: showEmojiPicker ? "block" : "none",
          zIndex: 1000,
          width: "350px",
          transform: "scale(0.7)",
          transformOrigin: "bottom left",
        }}
      >
        <emoji-picker ref={emojiPickerRef}></emoji-picker>
      </div>

      {/* Emoji button */}
      <img
        id="emojiButton"
        src="/icons/smileface.svg"
        alt="Emoji"
        style={{
          cursor: disabledButton ? "not-allowed" : "pointer",
          pointerEvents: disabledButton ? "none" : "auto",
          opacity: disabledButton ? 0.5 : 1,
        }}
        onClick={!disabledButton ? toggleEmojiPicker : null}
      />

      {/* Paperclip icon for file attachments */}
      <div>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/*,video/*,.pdf"
          onChange={handleFileUpload}
          disabled={disabledButton}
        />
        <img
          src="/icons/paperclip.svg"
          alt=""
          style={{
            cursor: disabledButton ? "not-allowed" : "pointer",
            pointerEvents: disabledButton ? "none" : "auto",
            opacity: disabledButton ? 0.5 : 1,
          }}
          onClick={() => {
            if (!disabledButton) {
              fileInputRef.current?.click();
            }
          }}
        />
      </div>
    </div>
  );
};

const ChatFooter = ({
  handleCloseEditor,
  handleSend,
  isEditing,
  isReplying,
  disabledButton,
}) => {
  return (
    <div
      style={{
        marginTop: "10px",
        margin: "20px",
        textAlign: "right",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <button
        onClick={handleCloseEditor}
        className="btn-1-basic btn-1 text-body-b1"
        disabled={disabledButton}
        style={{
          cursor: disabledButton ? "not-allowed" : "pointer",
          opacity: disabledButton ? 0.5 : 1,
        }}
      >
        {isEditing ? "Cancel" : isReplying ? "Cancel Reply" : "Close"}
      </button>
      <button
        id="sendExpButton"
        className="btn-1 btn-1-primary text-body-b1"
        disabled={disabledButton}
        onClick={handleSend}
        style={{
          cursor: disabledButton ? "not-allowed" : "pointer",
          opacity: disabledButton ? 0.5 : 1,
        }}
      >
        <svg
          width="17"
          height="14"
          viewBox="0 0 17 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.4752 0.0978383L1.33985 5.79534C0.375167 6.20034 0.380748 6.76284 1.16286 7.01367L4.79198 8.197L13.1887 2.6595C13.5857 2.407 13.9485 2.54284 13.6503 2.8195L6.84731 9.237H6.84571L6.84731 9.23784L6.59697 13.1478C6.96371 13.1478 7.12555 12.972 7.33124 12.7645L9.09398 10.9728L12.7606 13.8037C13.4366 14.1928 13.9222 13.9928 14.0904 13.1495L16.4973 1.29284C16.7437 0.260338 16.1202 -0.207162 15.4752 0.0978383Z"
            fill="white"
          />
        </svg>
        {isEditing ? "Update" : isReplying ? "Reply" : "Send"}
      </button>
    </div>
  );
};

export default ChatMessageBox;
